import { ChangeDetectionStrategy, Component, DOCUMENT, effect, ElementRef, inject, input } from '@angular/core';

/** El formato en que `lucide` exporta cada ícono: `[etiqueta SVG, atributos][]`. */
export type IconNode = readonly (readonly [string, Readonly<Record<string, string | number | undefined>>])[];

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * Ícono lineal en `currentColor`. Es decorativo salvo que traiga `label`; un botón de sólo ícono lleva
 * su nombre en el botón (`IconButtonComponent`), no aquí. El tamaño va en rem para crecer con el texto.
 */
@Component({
  selector: 'ui-icon',
  template: '',
  styleUrl: './icon.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IconComponent {
  readonly icon = input.required<IconNode>();
  readonly size = input('1.5rem');
  readonly strokeWidth = input(2);
  readonly label = input<string>();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly document = inject(DOCUMENT);

  constructor() {
    effect(() => this.render());
  }

  private render(): void {
    const svg = this.document.createElementNS(SVG_NS, 'svg');
    const size = this.size();
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', String(this.strokeWidth()));
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.style.width = size;
    svg.style.height = size;

    const label = this.label();
    if (label) {
      svg.setAttribute('role', 'img');
      svg.setAttribute('aria-label', label);
    } else {
      svg.setAttribute('aria-hidden', 'true');
    }

    for (const [tag, attrs] of this.icon()) {
      const child = this.document.createElementNS(SVG_NS, tag);
      for (const [name, value] of Object.entries(attrs)) {
        if (value !== undefined) child.setAttribute(name, String(value));
      }
      svg.appendChild(child);
    }

    this.host.replaceChildren(svg);
  }
}
